import React from "react";
import { UserContext } from "context/User";
import { makeAxiosCall, formatMoney } from "utils";
import { Button } from "@mui/material";
import { useNavigate } from "react-router-dom";
import Loading from "components/utils/Loading";

interface MeterData {
    total: number;
    total3yr: number;
    count: number;
    lastSoDate: string | null;
    met: boolean;
    unlockThreshold: number;
    newTarget: number;
    existingTarget: number;
}

interface PurchaseMeterProps {
    // Reports the cumulative-vs-unlock "met" state up to the Wizard so it can
    // unlock the Sign Reseller Agreement step.
    onMetChange?: (met: boolean) => void;
}

const fmt = (n: number) => formatMoney(n || 0, { cents: false });

const PurchaseMeter = ({ onMetChange }: PurchaseMeterProps) => {
    const { currentUser } = React.useContext(UserContext);
    const navigate = useNavigate();
    const [meter, setMeter] = React.useState<MeterData | null>(null);
    const [loading, setLoading] = React.useState<boolean>(true);

    // Provisional resellers (new onboarding + Bucket-B cutover) target the $2,500
    // initial-purchase amount (cumulative). Fully-approved resellers (e.g. mid-
    // renewal) target the $5,000 rolling-3yr quota. The agreement unlocks at
    // cumulative >= unlockThreshold regardless of which target is displayed.
    const isProvisional = !!currentUser?.company?.provisionalAccess;

    const loadMeter = async () => {
        setLoading(true);
        try {
            const res = await makeAxiosCall("get", "sales-orders/purchase-meter");
            setMeter(res.data);
            if (onMetChange) onMetChange(!!res.data.met);
        } catch (e) {
            console.log("purchase meter error", e);
        } finally {
            setLoading(false);
        }
    };

    React.useEffect(() => {
        loadMeter();
    }, []);

    if (loading || !meter) {
        return <Loading height="30vh" position="relative" title="Checking your orders..." />;
    }

    const target = isProvisional ? meter.newTarget : meter.existingTarget;
    const value = isProvisional ? meter.total : meter.total3yr;
    const pct = Math.min(100, target ? (value / target) * 100 : 0);
    const remaining = Math.max(0, target - value);
    // Soften the language once they already have qualifying purchases on file.
    const hasPurchases = value > 0;

    return (
        <div style={{ maxWidth: 640, margin: "auto", textAlign: "center" }}>
            <h2>{meter.met ? "You're qualified!" : hasPurchases ? "Qualifying Purchases" : "Make Your First Purchase"}</h2>
            <p style={{ fontSize: 16 }}>
                {meter.met ? (
                    <>You&apos;ve met the {fmt(meter.unlockThreshold)} minimum and you can now sign the Reseller Agreement.</>
                ) : hasPurchases ? (
                    <>
                        You have <strong>{fmt(value)}</strong> in qualifying purchases toward the{" "}
                        <strong>{fmt(target)}</strong> required. <strong>{fmt(remaining)}</strong> to go. A single
                        order or several, both count.
                    </>
                ) : (
                    <>
                        Place an initial order of <strong>{fmt(target)}</strong> to activate your reseller account. You
                        can do it in a single order or build up to it. You have <strong>{fmt(remaining)}</strong> to go.
                    </>
                )}
            </p>

            <p style={{ fontWeight: "bold", marginTop: 20, marginBottom: 8 }}>
                {fmt(value)} / {fmt(target)}
                {meter.count > 0 && (
                    <span style={{ fontWeight: "normal", color: "#828282" }}>
                        {"  "}({meter.count} qualifying order{meter.count === 1 ? "" : "s"})
                    </span>
                )}
            </p>
            <div
                className="purchase-meter-track"
                style={{
                    height: 24,
                    width: "100%",
                    maxWidth: 480,
                    margin: "0 auto",
                    background: "#e9e9f7",
                    borderRadius: 12,
                    overflow: "hidden",
                }}
            >
                <div
                    className="purchase-meter-fill"
                    style={{
                        width: pct + "%",
                        height: "100%",
                        background: meter.met ? "#27ae60" : "#282bf6",
                        transition: "width .4s ease",
                    }}
                ></div>
            </div>

            <div className="btn-group" style={{ marginTop: 20 }}>
                <Button
                    variant="contained"
                    color="secondary"
                    className="btn"
                    style={{ width: "100%" }}
                    onClick={() => loadMeter()}
                >
                    Refresh
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    className="btn"
                    style={{ width: "100%" }}
                    onClick={() => navigate("/products")}
                >
                    {meter.met ? "Browse Products" : "Place an Order"}
                </Button>
            </div>
        </div>
    );
};

export default PurchaseMeter;
